import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, TrendingUp, PiggyBank, Rocket, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getStructure, submitScan } from "@/lib/api";
import { Seo } from "@/components/Seo";

const INDUSTRIES = ["CPG/FMCG", "Retail", "Hi-Tech/Electronics", "Life Sciences/Pharma", "Manufacturing", "Logistics", "Other"];
const SIZES = ["Under 500 employees", "500-2000", "2000-10000", "Over 10000"];
const GEOS = ["India", "USA", "UK", "Europe", "Middle East", "Southeast Asia", "Other"];

const PRIORITIES = [
  { id: "growth", icon: TrendingUp, title: "Growth Focus", desc: "We need better forecast accuracy and service levels to support business growth." },
  { id: "cost", icon: PiggyBank, title: "Cost Reduction Focus", desc: "We need to reduce inventory and planning costs significantly." },
  { id: "transformation", icon: Rocket, title: "Transformation Focus", desc: "We are doing a full digital or AI transformation of our planning function." },
];

const OPTIONS = [
  { key: "A", label: "Just Starting", color: "#EF4444" },
  { key: "B", label: "Getting There", color: "#F59E0B" },
  { key: "C", label: "Capable", color: "#3B82F6" },
  { key: "D", label: "Best in Class", color: "#1A6B5A" },
];

const emptyProfile = { name: "", email: "", company: "", job_title: "", industry: "", company_size: "", geography: "" };

export default function Scan() {
  const navigate = useNavigate();
  const [structure, setStructure] = useState(null);
  const [step, setStep] = useState(0); // 0 welcome,1 profile,2 priority,3 tabs,4+ questions
  const [profile, setProfile] = useState(emptyProfile);
  const [priority, setPriority] = useState("");
  const [selected, setSelected] = useState([]);
  const [answers, setAnswers] = useState({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getStructure().then((d) => setStructure(d.functions)).catch(() => toast.error("Couldn't load the assessment."));
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  // Build question pages (one per L2 of selected functions, in structure order)
  const pages = useMemo(() => {
    if (!structure) return [];
    const out = [];
    structure.forEach((f) => {
      if (!selected.includes(f.id)) return;
      f.l2.forEach((l2, l2Index) => out.push({ fId: f.id, fName: f.name, l2Index, l2 }));
    });
    return out;
  }, [structure, selected]);

  const questionStartStep = 4;
  const totalQuestionPages = pages.length;
  const currentPageIndex = step - questionStartStep;
  const isLastQuestionPage = step >= questionStartStep && currentPageIndex === totalQuestionPages - 1;

  const qid = (fId, l2Index, qIndex) => `${fId}::${l2Index}::${qIndex}`;

  const setAnswer = (id, val) => setAnswers((a) => ({ ...a, [id]: val }));

  const toggleFunction = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const profileValid = Object.values(profile).every((v) => v.trim() !== "");

  const currentPageAnswered = () => {
    if (currentPageIndex < 0 || currentPageIndex >= pages.length) return false;
    const p = pages[currentPageIndex];
    return p.l2.questions.every((_, qi) => answers[qid(p.fId, p.l2Index, qi)]);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const data = await submitScan({
        profile, business_priority: priority,
        selected_functions: selected, answers,
      });
      navigate(`/results/free/${data.result_id}`);
    } catch (e) {
      toast.error("Submission failed. Please review your answers and try again.");
      setSubmitting(false);
    }
  };

  const next = () => {
    if (step < questionStartStep + totalQuestionPages - 1) setStep((s) => s + 1);
    else handleSubmit();
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const canProceed =
    step === 0 ? true :
    step === 1 ? profileValid :
    step === 2 ? !!priority :
    step === 3 ? selected.length > 0 :
    currentPageAnswered() && (!isLastQuestionPage || acknowledged);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Seo
        title="Intelligent SCM Scan | Free Supply Chain Planning Maturity Assessment"
        description="Take the free Intelligent SCM Scan to benchmark your maturity across Demand Planning, Inventory Planning, Supply Planning, and S&OP in minutes."
        path="/scan"
      />
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 md:px-8 py-12">
        {/* WELCOME */}
        {step === 0 && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="scan-welcome">
            <p className="text-sm uppercase tracking-[0.2em] text-[#1A6B5A] font-semibold mb-4">Intelligent SCM Scan</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-[#1B3A6B] leading-tight">
              Know exactly where your supply chain planning stands.
            </h1>
            <p className="mt-4 text-lg text-[#3E577A]">Takes 15-20 minutes.</p>
            <ul className="mt-8 space-y-4">
              {[
                "Free maturity score across all four planning functions",
                "AI-powered diagnosis of your biggest gaps",
                "Transformation Priority Matrix showing where to focus first",
                "Unlock full AI diagnostic report for ₹4,999 / $59",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-0.5 h-6 w-6 rounded-full bg-[#E6F2EF] flex items-center justify-center shrink-0">
                    <Check className="h-3.5 w-3.5 text-[#1A6B5A]" />
                  </span>
                  <span className="text-[#1B3A6B]">{b}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid="scan-start-btn" onClick={() => setStep(1)}
              className="mt-10 bg-[#1A6B5A] text-white hover:bg-[#135245] transition-colors rounded-sm px-7 py-3.5 font-semibold inline-flex items-center gap-2"
            >
              Start Assessment <ArrowRight className="h-4 w-4" />
            </button>
            <p className="mt-6 text-sm text-[#94A3B8]">
              Used by supply chain leaders across CPG, Retail, Hi-Tech, and Life Sciences.
            </p>
          </motion.div>
        )}

        {/* PROFILE */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="scan-profile">
            <StepLabel current={1} total={3} />
            <h2 className="font-heading text-3xl font-bold text-[#1B3A6B] mb-8">Tell us about you</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full name *"><Input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></Field>
              <Field label="Email address *"><Input data-testid="profile-email" type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} /></Field>
              <Field label="Company name *"><Input data-testid="profile-company" value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} /></Field>
              <Field label="Job title *"><Input data-testid="profile-jobtitle" value={profile.job_title} onChange={(e) => setProfile({ ...profile, job_title: e.target.value })} /></Field>
              <Field label="Industry *">
                <DropdownField testid="profile-industry" value={profile.industry} placeholder="Select industry" options={INDUSTRIES} onChange={(v) => setProfile({ ...profile, industry: v })} />
              </Field>
              <Field label="Company size *">
                <DropdownField testid="profile-size" value={profile.company_size} placeholder="Select size" options={SIZES} onChange={(v) => setProfile({ ...profile, company_size: v })} />
              </Field>
              <Field label="Geography *">
                <DropdownField testid="profile-geo" value={profile.geography} placeholder="Select geography" options={GEOS} onChange={(v) => setProfile({ ...profile, geography: v })} />
              </Field>
            </div>
          </motion.div>
        )}

        {/* PRIORITY */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="scan-priority">
            <StepLabel current={2} total={3} />
            <h2 className="font-heading text-3xl font-bold text-[#1B3A6B] mb-8">What is your primary business focus right now?</h2>
            <div className="space-y-4">
              {PRIORITIES.map((p) => {
                const active = priority === p.id;
                return (
                  <button
                    key={p.id} data-testid={`priority-${p.id}`} onClick={() => setPriority(p.id)}
                    className={`w-full text-left rounded-sm border-2 p-6 flex gap-4 transition-all hover:-translate-y-0.5 ${
                      active ? "border-[#1A6B5A] bg-[#E6F2EF]" : "border-[#E5E7EB] bg-[#F4F4F4] hover:border-[#1A6B5A]/50"
                    }`}
                  >
                    <p.icon className={`h-7 w-7 shrink-0 ${active ? "text-[#1A6B5A]" : "text-[#3E577A]"}`} />
                    <div>
                      <h3 className="font-heading font-bold text-[#1B3A6B]">{p.title}</h3>
                      <p className="text-sm text-[#3E577A] mt-1">{p.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* TABS */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="scan-tabs">
            <StepLabel current={3} total={3} />
            <h2 className="font-heading text-3xl font-bold text-[#1B3A6B] mb-8">Which planning functions do you want to assess today?</h2>
            <div className="space-y-4">
              {structure?.map((f) => {
                const active = selected.includes(f.id);
                return (
                  <button
                    key={f.id} data-testid={`tab-${f.id}`} onClick={() => toggleFunction(f.id)}
                    className={`w-full text-left rounded-sm border-2 p-5 flex items-start gap-4 transition-all ${
                      active ? "border-[#1A6B5A] bg-[#E6F2EF]" : "border-[#E5E7EB] bg-[#F4F4F4] hover:border-[#1A6B5A]/50"
                    }`}
                  >
                    <span className={`mt-0.5 h-6 w-6 rounded-sm border-2 flex items-center justify-center shrink-0 ${
                      active ? "bg-[#1A6B5A] border-[#1A6B5A]" : "border-[#94A3B8]"
                    }`}>
                      {active && <Check className="h-4 w-4 text-white" />}
                    </span>
                    <div>
                      <h3 className="font-heading font-bold text-[#1B3A6B]">{f.name}</h3>
                      <p className="text-sm text-[#3E577A] mt-1">{f.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="mt-6 text-sm text-[#94A3B8]">
              You can assess one function or all four. A Demand Planning Manager typically completes Tab 1 only.
              A VP Supply Chain may complete all four.
            </p>
          </motion.div>
        )}

        {/* QUESTION PAGES */}
        {step >= questionStartStep && pages[currentPageIndex] && (
          <QuestionPage
            page={pages[currentPageIndex]}
            currentPageIndex={currentPageIndex}
            total={totalQuestionPages}
            answers={answers}
            setAnswer={setAnswer}
            qid={qid}
          />
        )}

        {/* RISK ACKNOWLEDGEMENT — required before submission */}
        {isLastQuestionPage && (
          <label
            data-testid="scan-acknowledgement"
            className="mt-10 flex items-start gap-3 rounded-sm border-2 border-[#E5E7EB] bg-[#F8FAFC] p-5 cursor-pointer hover:border-[#1A6B5A]/50 transition-colors"
          >
            <input
              type="checkbox"
              data-testid="scan-acknowledge-checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 h-5 w-5 shrink-0 accent-[#1A6B5A] cursor-pointer"
            />
            <span className="text-sm text-[#3E577A] leading-relaxed">
              I understand that assessment results are informational only and do not constitute
              consulting, legal, financial, or professional advice.
            </span>
          </label>
        )}

        {/* NAV */}
        {step > 0 && (
          <div className="mt-12 flex items-center justify-between border-t border-[#E5E7EB] pt-6">
            <button
              data-testid="scan-prev-btn" onClick={prev}
              className="inline-flex items-center gap-2 text-[#3E577A] font-medium hover:text-[#1B3A6B]"
            >
              <ArrowLeft className="h-4 w-4" /> Previous
            </button>
            <button
              data-testid="scan-next-btn" onClick={next} disabled={!canProceed || submitting}
              className="inline-flex items-center gap-2 bg-[#1A6B5A] text-white hover:bg-[#135245] transition-colors rounded-sm px-7 py-3 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Scoring…</>) :
                step >= questionStartStep && currentPageIndex === totalQuestionPages - 1 ?
                  (<>See my results <ArrowRight className="h-4 w-4" /></>) :
                  (<>Next <ArrowRight className="h-4 w-4" /></>)}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

const StepLabel = ({ current, total }) => (
  <p className="text-xs uppercase tracking-[0.18em] text-[#1A6B5A] font-semibold mb-3">Step {current} of {total}</p>
);

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-sm font-medium text-[#1B3A6B]">{label}</Label>
    {children}
  </div>
);

const DropdownField = ({ value, placeholder, options, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

const QuestionPage = ({ page, currentPageIndex, total, answers, setAnswer, qid }) => {
  const progress = Math.round(((currentPageIndex) / total) * 100);
  return (
    <motion.div key={currentPageIndex} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} data-testid="scan-questions">
      <div className="mb-2 flex items-center justify-between text-xs text-[#3E577A]">
        <span>Page {currentPageIndex + 1} of {total}</span>
        <span>{progress}% complete</span>
      </div>
      <Progress value={progress} className="h-1.5 mb-6 bg-[#F4F4F4]" />
      <p className="text-xs uppercase tracking-[0.18em] text-[#1A6B5A] font-semibold mb-1">{page.fName}</p>
      <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1B3A6B] mb-8">{page.l2.name}</h2>

      <div className="space-y-8">
        {page.l2.questions.map((q, qi) => {
          const id = qid(page.fId, page.l2Index, qi);
          return (
            <div key={id} className="border border-[#E5E7EB] rounded-sm p-6 bg-white" data-testid={`question-${qi}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-bold text-white bg-[#1B3A6B] rounded-full h-6 w-6 flex items-center justify-center">{qi + 1}</span>
                <span className={`text-xs font-semibold uppercase tracking-wider ${q.parameter === "Complexity" ? "text-[#3E577A]" : "text-[#1A6B5A]"}`}>
                  {q.parameter} · {q.label}
                </span>
              </div>
              <p className="font-semibold text-[#1B3A6B] mb-4 leading-snug">{q.text}</p>
              <div className="space-y-3">
                {OPTIONS.map((opt) => {
                  const selected = answers[id] === opt.key;
                  return (
                    <button
                      key={opt.key} data-testid={`q${qi}-option-${opt.key}`}
                      onClick={() => setAnswer(id, opt.key)}
                      className={`w-full text-left rounded-sm border-2 p-4 flex gap-3 transition-all ${
                        selected ? "border-[#1A6B5A] bg-[#E6F2EF]" : "border-[#E5E7EB] hover:border-[#1A6B5A]/40 bg-white"
                      }`}
                    >
                      <span className="shrink-0 text-xs font-bold px-2 py-1 rounded-sm text-white h-fit" style={{ background: opt.color }}>
                        {opt.key} · {opt.label}
                      </span>
                      <span className="text-sm text-[#3E577A] leading-relaxed">{q.options[opt.key]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};
